<template>
  <div v-if="positionsInitialLoading" class="table-group">
    <div class="table-loader"></div>
  </div>

  <div v-if="positionsError" class="mc-error">{{ positionsError }}</div>

  <template v-if="positionsLoaded">
    <div class="header-wrapper header-wrapper--row shipment-page-header">
      <div class="header-group flex-column">
        <div class="flex">
          <div class="h1">{{ t('pageTitleOrdersAll') }}</div>
        </div>
      </div>
      <div class="buttons-row">
        <button v-if="canImportShipment" class="btn" type="button" @click="openShipmentImport">{{ t('shipmentImportPages.upload') }}</button>
        <a class="btn" @click="showDates = true"> {{ t('orderPage.importExcel') }}</a>
        <a class="btn" :class="{loader:processExport}" @click="exportExcel"> {{ t('orderPage.exportExcel') }}</a>
      </div>
    </div>

    <div v-if="shipmentNotice" class="download-zone-note" role="status">
      <p>{{ shipmentNotice }}</p>
      <button class="btn-outline" type="button" :disabled="shipmentRefreshing" @click="refreshShipmentTable()">{{ t('shipmentImportPages.refresh') }}</button>
    </div>
    <ShipmentImportDialog
      :is-open="showShipmentImport"
      :scope="shipmentScope"
      :report-detached-cleanup-error="onShipmentCleanupError"
      @close="showShipmentImport = false"
      @completed="onShipmentCompleted"
      @cleanup-error="onShipmentCleanupError"
    />

    <Positions
        ref="positionsTable"
        @refresh="refreshShipmentTable"
        mode="all"
        :order="{}"
        :positions="positions"
        :pagination="positionsPagination"
        :server-sort="positionsSort"
        :filter-options="filterOptions"
        :initial-filters="currentFilters()"
        :loading="positionsLoading"
        :orderLoaded="positionsLoaded"
        @change-page="changePage"
        @change-sort="changeSort"
        @change-filters="changeFilters"
    ></Positions>

    <Dates :isOpen="showDates" @close="showDates = false" @imported="handlePositionsImported" />
  </template>
</template>

<script>
import {mapActions, mapGetters} from "vuex";

import Dates from '@/components/order/Dates.vue';
import Positions from '@/components/order/Positions.vue';
import importexport from '@/mixin/importexport.js';
import shipmentImportPage from '@/mixin/shipmentImportPage';

import { useI18n } from 'vue-i18n';

const POSITION_FILTERS_STORAGE_KEY = 'sumiteclk:position-list-filters:position';

export default {
  components: {
    Positions,
    Dates
  },
  mixins: [importexport, shipmentImportPage],
  data: () => ({
    showAttachment: false,
    showHistory: false,
    showEdit: false,
    showSettings: false,
    showDates: false,
    isInitializingSearch: true,

    sortKey: '',
    sortDir: 'asc',

    filtersState: {
      selectedStorage_delivery: [],
      selectedEmpty_date: [],
      selectedFilled_date: [],
      showArrived: false,
      invoiceOrderSearch: '',
      selectedFilial: [],
      selectedPartner: [],
      selectedColor: [],
      selectedMarking: [],
    },
    datesOptions: [
      {
        label: 'dates.dateOrderShipment',
        value: 'date_order_shipment'
      },
      {
        label: 'dates.dateSupplierOrder',
        value: 'date_supplier_order'
      },
      {
        label: 'dates.dateArrivalSupplier',
        value: 'date_arrival_supplier'
      },
      {
        label: 'dates.dateReadyShipment',
        value: 'date_ready_shipment'
      },
      {
        label: 'dates.datePlannedArrival',
        value: 'date_planned_arrival'
      }
    ],
    empty_dateOptions: [
      {
        label: 'dates.dateOrderShipment',
        value: 'date_order_shipment'
      },
      {
        label: 'dates.dateSupplierOrder',
        value: 'date_supplier_order'
      },
      {
        label: 'dates.dateArrivalSupplier',
        value: 'date_arrival_supplier'
      },
    ]

  }),
  setup() {
    const { t } = useI18n();
    return { t };
  },
  created() {
    this.initializeSearch();
  },
  watch: {
    async '$route.query'() {
      if (this.isInitializingSearch) {
        return;
      }

      if (Object.prototype.hasOwnProperty.call(this.$route.query, 'f_order')
        || Object.keys(this.$route.query).some(key => key.startsWith('f_date_'))) {
        await this.$router.replace({
          name: this.$route.name,
          query: this.withoutRemovedOrderFilter(this.$route.query),
        });
        return;
      }

      this.saveSearch(this.searchFromQuery());
      this.load();
    },
  },
  computed: {
    ...mapGetters('positions', [
      'positions',
      'positionsPermissions',
      'positionsPagination',
      'positionsSort',
      'filterOptions',
      'positionsLoading',
      'positionsError',
      'positionsLoaded',
    ]),
    canImportShipment() { return !this.positionsError && this.positionsPermissions.can_import_shipment === true; },
    positionsInitialLoading() {
      return this.positionsLoading && !this.positionsLoaded;
    },
  },
  methods: {
    ...mapActions('positions', [
      'fetchPositions',
    ]),
    async initializeSearch() {
      const queryHasSearch = Object.prototype.hasOwnProperty.call(this.$route.query, 'f_search');
      const queryHasRemovedFilters = Object.prototype.hasOwnProperty.call(this.$route.query, 'f_order')
        || Object.keys(this.$route.query).some(key => key.startsWith('f_date_'));
      const search = queryHasSearch ? this.searchFromQuery() : this.restoreSearch();

      this.saveSearch(search);

      if (queryHasRemovedFilters || (!queryHasSearch && search)) {
        const normalizedQuery = this.withoutRemovedOrderFilter(this.$route.query);

        if (!queryHasSearch && search) {
          normalizedQuery.f_search = search;
        }

        await this.$router.replace({
          name: this.$route.name,
          query: normalizedQuery,
        });
        await this.$nextTick();
      }

      this.isInitializingSearch = false;
      await this.load();
    },
    withoutRemovedOrderFilter(query) {
      const normalizedQuery = {...query};
      delete normalizedQuery.f_order;
      Object.keys(normalizedQuery).forEach(key => {
        if (key.startsWith('f_date_')) delete normalizedQuery[key];
      });
      return normalizedQuery;
    },
    searchFromQuery() {
      const value = this.$route.query.f_search;
      if (Array.isArray(value)) {
        return String(value[0] || '');
      }
      return String(value || '');
    },
    restoreSearch() {
      try {
        const rawState = sessionStorage.getItem(POSITION_FILTERS_STORAGE_KEY);
        if (!rawState) {
          return '';
        }

        const savedState = JSON.parse(rawState);
        if (typeof savedState === 'string') {
          return savedState;
        }
        return typeof savedState?.search === 'string' ? savedState.search : '';
      } catch (error) {
        console.warn('Не удалось восстановить поиск развёрнутого списка заказов:', error);
        return '';
      }
    },
    saveSearch(search) {
      try {
        const value = String(search || '');
        if (!value) {
          sessionStorage.removeItem(POSITION_FILTERS_STORAGE_KEY);
          return;
        }
        sessionStorage.setItem(POSITION_FILTERS_STORAGE_KEY, JSON.stringify({search: value}));
      } catch (error) {
        console.warn('Не удалось сохранить поиск развёрнутого списка заказов:', error);
      }
    },
    async load() {
      return this.fetchPositions({
        page: this.currentPage(),
        sort: this.currentSort(),
        sortDir: this.currentSortDir(),
        filters: this.currentFilterParams(),
      });
    },
    currentPage() {
      return parseInt(this.$route.query.page, 10) || 1;
    },
    changePage(page) {
      if (page === this.currentPage()) {
        return;
      }

      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page,
        },
      });
    },
    currentSort() {
      return this.$route.query.sort || '';
    },
    currentSortDir() {
      return this.$route.query.sort_dir === 'desc' ? 'desc' : 'asc';
    },
    changeSort(sort) {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page: 1,
          sort: sort.key || undefined,
          sort_dir: sort.dir || undefined,
        },
      });
    },
    changeFilters(filters) {
      this.saveSearch(filters.search);
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.queryWithoutFilters(),
          page: 1,
          ...this.buildFilterQuery(filters),
        },
      });
    },
    currentFilters() {
      const query = this.$route.query;

      return {
        partner: this.queryArray(query.f_partner),
        manager: this.queryArray(query.f_manager),
        filial: this.queryArray(query.f_filial),
        color: this.queryArray(query.f_color),
        storage_delivery: this.queryArray(query.f_storage_delivery),
        marking: this.queryArray(query.f_marking),
        empty_date: this.queryArray(query.f_empty_date),
        filled_date: this.queryArray(query.f_filled_date),
        arrived: query.f_arrived === '1',
        search: this.searchFromQuery(),
      };
    },
    currentFilterParams() {
      return this.buildFilterQuery(this.currentFilters());
    },
    queryArray(value) {
      if (Array.isArray(value)) {
        return value.filter(item => item !== '');
      }
      return value ? [value] : [];
    },
    queryWithoutFilters() {
      return Object.entries(this.$route.query).reduce((result, [key, value]) => {
        if (key !== 'page' && !key.startsWith('f_')) {
          result[key] = value;
        }
        return result;
      }, {});
    },
    buildFilterQuery(filters) {
      const query = {
        f_partner: filters.partner?.length ? filters.partner : undefined,
        f_manager: filters.manager?.length ? filters.manager : undefined,
        f_filial: filters.filial?.length ? filters.filial : undefined,
        f_color: filters.color?.length ? filters.color : undefined,
        f_storage_delivery: filters.storage_delivery?.length ? filters.storage_delivery : undefined,
        f_marking: filters.marking?.length ? filters.marking : undefined,
        f_empty_date: filters.empty_date?.length ? filters.empty_date : undefined,
        f_filled_date: filters.filled_date?.length ? filters.filled_date : undefined,
        f_arrived: filters.arrived ? '1' : undefined,
        f_search: filters.search || undefined,
      };

      return query;
    },
  }
}
</script>
<style>
.allpositions .table td {padding: 6px 10px;}
</style>
